import React, { useState, useEffect, useRef } from 'react';
import "./MultiSelect.css";
import "./SingleSelect.css";
import BodySmallRegular from './BodySmallRegular';
import { Search, ChevronDown, ChevronRight } from "lucide-react";
import { CircleAlert } from "lucide-react";
import { formConfigs } from '../addNew/formConfigs';
import { t } from '../../locales/translate';
const MultiSelect = ({
    options = [],
    value = [],
    onChange,
    disabled = false,
    placeholder = `${t("select")}`,
    label,
    isSearchable,
    secondaryLabel,
    icon,
    iconStart,
    iconEnd,
    error,
    tooltipExample, tooltipFormat, labelIconRight,
     onAddNewClick,
    configKey,
    showSubordinateToggle = false,
    onSubordinateToggle,
    isSubordinateOnly = false,
    onInputChange,
    isLoading = false,
    menuIsOpen,              
    onMenuOpen,              
    onMenuClose,
    onScrollBottom,
    isLoadingMore = false,
    scrollLoadOffset = 30,
}) => {
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [isOpen, setIsOpen] = useState(menuIsOpen !== undefined ? menuIsOpen : false);
    const [searchQuery, setSearchQuery] = useState('');
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);
    const [filteredOptions, setFilteredOptions] = useState(options);
    const containerRef = useRef(null);
    const [visibleChips, setVisibleChips] = useState([]);
    const [overflowCount, setOverflowCount] = useState(0);
    const [showTooltip, setShowTooltip] = useState(false);
    const [expandedSubtexts, setExpandedSubtexts] = useState({});
    const [expandedLocations, setExpandedLocations] = useState({});
    
    const [dropdownSessionOptions, setDropdownSessionOptions] = useState([]);
    const [isDropdownSession, setIsDropdownSession] = useState(false);
    const labelMapRef = useRef({});

    const config = configKey ? formConfigs[configKey] || {} : null;
    const permissions = JSON.parse(localStorage.getItem("permissionsList")) || [];
    const canAdd =
      !!configKey && (!config?.requiredPermission || permissions.includes(config.requiredPermission));
    
    const handleToggleDropdown = () => {
        if (!disabled) {
            const newState = !isOpen;
            setIsOpen(newState);
            if (newState && onMenuOpen) {
                onMenuOpen();
            } else if (!newState && onMenuClose) {
                onMenuClose();
            }
        }
    };
    
    const toggleSubtextExpand = (e, optionValue) => {
        e.stopPropagation(); 
        setExpandedSubtexts(prev => ({
            ...prev,
            [optionValue]: !prev[optionValue]
        }));
    };

    const toggleLocationExpand = (e, optionValue) => {
        e.stopPropagation();
        setExpandedLocations(prev => ({
            ...prev,
            [optionValue]: !prev[optionValue]
        }));
    };

    const handleSubordinateClick = (e) => {
    e.stopPropagation(); 
    if (onSubordinateToggle) {
        onSubordinateToggle(!isSubordinateOnly);
    }
};
    useEffect(() => {
        if (menuIsOpen !== undefined) {
            setIsOpen(menuIsOpen);
        }
    }, [menuIsOpen]);

        useEffect(() => {
        const handleClickOutside = (event) => {
            if (inputRef.current && inputRef.current.contains(event.target)) {
                const newState = !isOpen;
                setIsOpen(newState);
                if (newState && onMenuOpen) {
                    onMenuOpen();
                } else if (!newState && onMenuClose) {
                    onMenuClose();
                }
            }
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                if (inputRef.current && !inputRef.current.contains(event.target)) {
                    setIsOpen(false);
                    if (onMenuClose) {
                        onMenuClose();
                    }
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onMenuOpen, onMenuClose]);

    useEffect(() => {
        if (isOpen) {
            if (window.getSelection) {
                window.getSelection().removeAllRanges();
            }
            if (isSearchable && searchInputRef.current) {
                setTimeout(() => {
                    searchInputRef.current.focus();
                }, 10);
            }
            const preventSelection = (e) => {
                if (searchInputRef.current && !searchInputRef.current.contains(e.target) &&
                    dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                    e.preventDefault();
                }
            };
            
            document.addEventListener('selectstart', preventSelection);
            return () => document.removeEventListener('selectstart', preventSelection);
        }
        if (!isOpen) {
            setSearchQuery('');
        }
    }, [isOpen, isSearchable]);

    useEffect(() => {
        if (isOpen) {
            if (!isDropdownSession || options.length !== dropdownSessionOptions.length) {
                const sortedOptions = [...options].sort((a, b) => {
                    const aSelected = Array.isArray(value) && value.includes(a.value);
                    const bSelected = Array.isArray(value) && value.includes(b.value);
                    if (aSelected && !bSelected) return -1;
                    if (!aSelected && bSelected) return 1;
                    return 0;
                });
                setDropdownSessionOptions(sortedOptions);
                setIsDropdownSession(true);
            }
        } else {
            setIsDropdownSession(false);
        }
    }, [isOpen, options, value, dropdownSessionOptions.length, isDropdownSession]);
    
    useEffect(() => {
        let sourceOptions = isDropdownSession ? dropdownSessionOptions : options;
    
        let filtered = sourceOptions;
        if (searchQuery.trim()) {
            const lowerCaseQuery = searchQuery.toLowerCase();
            filtered = sourceOptions.filter((option) => 
                option.label.toLowerCase().includes(lowerCaseQuery) || 
                (option.subtext && option.subtext.toLowerCase().includes(lowerCaseQuery))
            );
        }
        
        setFilteredOptions(filtered);
    }, [searchQuery, options, dropdownSessionOptions, isDropdownSession, isOpen]);

    const handleOptionClick = (option) => {
        const currentValues = Array.isArray(value) ? value : [];
        let updatedValues;

        if (currentValues.includes(option.value)) {
            updatedValues = currentValues.filter((val) => val !== option.value);
        } else {
            updatedValues = [...currentValues, option.value];
        }

        if (onChange) {
            onChange(updatedValues, label);
        }
    };

    const handleCheckboxChange = (e, option) => {
        e.stopPropagation();
        handleOptionClick(option);
    };    
    const handleSearchInput = (e) => {
        e.stopPropagation();
        setSearchQuery(e.target.value);
         if (onInputChange) {
        onInputChange(e.target.value);
    }
    };

    useEffect(() => {
        // Accumulate value->label mapping so chips survive search filtering
        options.forEach(option => {
            labelMapRef.current[option.value] = option.label;
        });
        if (Array.isArray(value) && value.length > 0) {
            const matchedLabels = value
                .filter(v => labelMapRef.current[v] !== undefined)
                .map(v => labelMapRef.current[v]);
            setSelectedOptions(matchedLabels);
        } else {
            setSelectedOptions([]);
        }
    }, [value, options]);

    useEffect(() => {
        if (containerRef.current) {
            const containerWidth = containerRef.current.offsetWidth;
            let totalWidth = 0;
            let visible = [];
            let overflow = 0;

            const tempSpan = document.createElement('span');
            tempSpan.classList.add('inline-flex', 'items-center', 'px-2', 'py-1', 'rounded-md', 'bg-gray-100', 'text-sm');
            tempSpan.style.position = 'absolute';
            tempSpan.style.visibility = 'hidden';
            document.body.appendChild(tempSpan);

            selectedOptions.forEach((option) => {
                tempSpan.innerText = option;
                const chipWidth = tempSpan.offsetWidth;

                if (totalWidth + chipWidth < containerWidth) {
                    visible.push(option);
                    totalWidth += chipWidth;
                } else {
                    overflow++;
                }
            });

            document.body.removeChild(tempSpan);
            setVisibleChips(visible);
            setOverflowCount(overflow);
        }
    }, [selectedOptions]);
    return (
        <div className={`custom-multi-select-container ${disabled ? 'custom-disabled' : ''}`}
        >
            <div className='multi-select-main'>
                {label && label.length !== 0 && (
                    <div className="label-with-icons">
                        <p className="label-text">{label}</p>
                        {labelIconRight && (
                            <div
                                className="icon-with-tooltip"
                                onMouseEnter={() => setShowTooltip(true)}
                                onMouseLeave={() => setShowTooltip(false)}
                            >
                                {labelIconRight}
                                {showTooltip && (tooltipFormat || tooltipExample) && (
                                    <div className="tooltip-box">
                                        {tooltipFormat && (
                                            <div className="tooltip-row">
                                                <span className="tooltip-heading">Format:</span>
                                                <span className="tooltip-text">{tooltipFormat}</span>
                                            </div>
                                        )}
                                        {tooltipExample && (
                                            <div className="tooltip-row">
                                                <span className="tooltip-heading">Example:</span>
                                                <span className="tooltip-text">{tooltipExample}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
                <div className='custom-multi-select-div'>
                    <div className="custom-multi-select" ref={inputRef}>
                        <div className='selected-options-container' ref={containerRef}>
                            {visibleChips.length > 0
                                ? visibleChips.map((option, index) => (
                                    <span key={index} className='label-chip'>
                                        {option}
                                    </span>
                                ))
                                : <span className="placeholder-text">{placeholder}</span>}

                            {overflowCount > 0 && (
                                <span>+{overflowCount}</span>
                            )}
                        </div>
                        <span className="custom-multi-select-icon">
                            {icon ? icon : <ChevronDown />}
                        </span>
                    </div>
                    {error && error.trim().length > 0 && (
                        <div className="error-multi-select-container">
                            <CircleAlert height={16} />
                            <span className="multi-select-error-text">
                                {error}
                            </span>
                        </div>
                    )}

            {isOpen && (
                <div className="custom-multi-select-dropmenu-container" ref={dropdownRef}>
                    {isLoading || (options && options.length > 0) ? (
                        <>
    {showSubordinateToggle && (
    <div className="subordinate-toggle-container">
        <div className="toggle-row">
            <span className="form-toggle-label">Subordinates Only</span>
            <div
                className={`toggle-switch ${isSubordinateOnly ? "toggle-on" : "toggle-off"}`}
                onClick={(e) => {
                    e.stopPropagation();
                    if (onSubordinateToggle) {
                        onSubordinateToggle(!isSubordinateOnly);
                    }
                }}
                role="switch"
                aria-checked={isSubordinateOnly}
            >
                <div className="toggle-button" />
            </div>
        </div>
    </div>
)}
                            {isSearchable && (
    <div className='search-and-clear-wrapper'>
        <div className='custom-multi-select-search-container'>
            <input
                ref={searchInputRef}
                className='search-input'
                placeholder='Search'
                value={searchQuery}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                onChange={handleSearchInput}
            />
            <Search width={18} />
        </div>
            <button
                type="button"
                className="clear-all-button"
                onClick={(e) => {
                    e.stopPropagation();
                    if (onChange) {
                        onChange([], label);
                    }
                }}
            >
                Clear
            </button>
        
    </div>
)}
                            <div
    className="custom-multi-select-options"
    onScroll={onScrollBottom ? (e) => {
        const el = e.currentTarget;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - scrollLoadOffset) {
            onScrollBottom();
        }
    } : undefined}
>
    {isLoading ? (
        <div className="custom-multi-select-option">
            <span>Loading...</span>
        </div>
    ) : filteredOptions && filteredOptions.length > 0 ? (
        <>
            
            <div
                className="custom-multi-select-option select-all-option"
                onClick={() => {
                    const allValues = options.map(opt => opt.value);
                    const allSelected = allValues.every(val => value.includes(val));
                    if (onChange) {
                        onChange(allSelected ? [] : allValues, label);
                    }
                }}
            >
                <div className='primary-label-div'>
                    <div className="checkbox-container">
                        <input
                            type="checkbox"
                            className="custom-checkbox-input"
                            checked={options.length > 0 && options.every(opt => value.includes(opt.value))}
                            onChange={(e) => {
                                e.stopPropagation();
                                const allValues = options.map(opt => opt.value);
                                const allSelected = allValues.every(val => value.includes(val));
                                if (onChange) {
                                    onChange(allSelected ? [] : allValues, label);
                                }
                            }}
                        />
                    </div>
                    <div className="option-text-container">
                        <span className='label-text' >All</span>
                    </div>
                </div>
            </div>
            {filteredOptions.map((option) => (
        <div key={option.value} className="option-item-wrapper">
            <div
                className="custom-multi-select-option"
                onClick={() => handleOptionClick(option)}
            >
                <div className='primary-label-div'>
                    <div className="checkbox-container">
                        <input
                            type="checkbox"
                            className="custom-checkbox-input"
                            checked={Array.isArray(value) && value.includes(option.value)}
                            onChange={(e) => handleCheckboxChange(e, option)}
                        />
                    </div>
                    {iconStart && iconStart}
                    <div className="option-text-container">
                        <span className='label-text'>{option.label}</span>
                        {option.subtext && (
                            <span
                                className={`option-subtext ${expandedSubtexts[option.value] ? 'expanded' : ''}`}
                                title={option.subtext}
                                onClick={(e) => toggleSubtextExpand(e, option.value)}
                            >
                                {option.subtext}
                            </span>
                        )}
                    </div>
                </div>
                <div className="option-right-actions">
                    {secondaryLabel && option.secondaryLabel && (
                        <span className='secondary-label'>{option.secondaryLabel}</span>
                    )}
                    {iconEnd && iconEnd}
                    {option.locations?.length > 0 && (
                        <button
                            type="button"
                            className={`location-expand-btn ${expandedLocations[option.value] ? 'expanded' : ''}`}
                            onClick={(e) => toggleLocationExpand(e, option.value)}
                            title="Show practice locations"
                        >
                            {expandedLocations[option.value]
                                ? <ChevronDown size={14} />
                                : <ChevronRight size={14} />}
                        </button>
                    )}
                </div>
            </div>
            {option.locations?.length > 0 && expandedLocations[option.value] && (
                <div className="option-locations-panel" onClick={(e) => e.stopPropagation()}>
                    {option.locations.map((loc, i) => (
                        <div key={i} className="option-location-item">{loc}</div>
                    ))}
                </div>
            )}
        </div>
    ))}
    {isLoadingMore && (
        <div className="custom-multi-select-option" style={{ justifyContent: 'center', color: '#64748b', fontSize: '13px' }}>
            <span>Loading more...</span>
        </div>
    )}
        </>
    ) : (
        <div className="custom-multi-select-option">
            <span>No data</span>
        </div>
    )}
</div>
                            {onAddNewClick && canAdd && (
                    <div className="custom-single-select-add-new-container">
                        <button
                        type="button"
                        className="custom-single-select-add-new-button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onAddNewClick();
                        }}
                        >
                        {`+ ${t("addNew")}`}
                        </button>
                    </div>
                            )}
                        </>
                    ) : (
                        <div className="custom-multi-select-option">
                            <span>No data</span>
                        </div>
                    )}
                </div>
            )}
                </div>
            </div>

        </div>
    );
};
export default MultiSelect;
